import { generateKeyBetween } from 'fractional-indexing';

/**
 * Interface for a column with a position
 * Note: we just need spaceColumnId and columnIndex for ordering columns
 */
export interface ColumnWithPosition {
  spaceColumnId: string;
  columnIndex: string | null;
  [key: string]: unknown;
}

/**
 * Sorts columns by their fractional index position
 * @param columns - Array of columns to sort
 * @returns Sorted array of columns
 */
export const sortColumnsByPosition = <T extends ColumnWithPosition>(columns: T[]): T[] => {
  // check empty or null/undefined input
  if (!columns || columns?.length === 0) {
    return [];
  }

  // check for single item (no sorting needed)
  if (columns?.length === 1) {
    return [...columns];
  }

  return [...columns].sort((a, b) => {
    // check for null/undefined positions - put them at the end
    if (!a.columnIndex && !b.columnIndex) return 0;
    if (!a.columnIndex) return 1;
    if (!b.columnIndex) return -1;

    return a.columnIndex < b.columnIndex ? -1 : a.columnIndex > b.columnIndex ? 1 : 0;
  });
};

/**
 * Remove an item from a list by index (immutable)
 */
export const removeColumnAtIndex = <T>(arr: T[], index: number): T[] => arr.filter((_, i) => i !== index);

/**
 * Determine if two lists are the same reference
 */
export const isSameColumnList = <T>(listA: T[], listB: T[]) => listA === listB;

/**
 * Generate new position key based on prev/next positions
 */
export const getNewColumnPositionBetween = (
  prev: ColumnWithPosition | null,
  next: ColumnWithPosition | null
): string => {
  const prevPos = prev?.columnIndex ?? null;
  const nextPos = next?.columnIndex ?? null;
  return generateKeyBetween(prevPos, nextPos);
};

/**
 * Get new position at top of list
 * When inserting at the top (before all others)
 */
export const getColumnPositionAtTop = (columns: ColumnWithPosition[] | undefined): string => {
  const first = sortColumnsByPosition(columns ?? [])[0];
  return getNewColumnPositionBetween(null, first ?? null);
};

/**
 * When inserting at the bottom (after all others)
 */
export const getColumnPositionAtBottom = (columns: ColumnWithPosition[] | undefined): string => {
  const last = sortColumnsByPosition(columns ?? []).at(-1);
  return getNewColumnPositionBetween(last ?? null, null);
};

/**
 * Generate a new fractional position when reordering a column.
 *
 * Handles:
 * - Moving up or down within the same list
 * - Empty target lists
 */
export const reorderColumns = (
  sourceColumns: ColumnWithPosition[],
  targetColumns: ColumnWithPosition[],
  sourceIndex: number,
  targetIndex: number
): string => {
  // check if the move is within the same list
  const sameList = isSameColumnList(sourceColumns, targetColumns);

  // get the list to order
  const listToOrder = sameList ? sourceColumns : targetColumns;

  // sort the list by position to maintain the order
  const ordered = listToOrder;

  if (sameList) {
    // remove the column from the list
    const [moved] = ordered.splice(sourceIndex, 1);

    // insert the column at the target index
    ordered.splice(targetIndex, 0, moved);
  }

  // get the previous and next columns to calculate the new position
  const prev = ordered[targetIndex - 1] ?? null;
  const next = ordered[targetIndex + (sameList ? 1 : 0)] ?? null;

  return getNewColumnPositionBetween(prev, next);
};
